import { useEffect, useState } from "react";

// Persists draggable desktop-icon positions to localStorage, keyed by `storageKey` so
// multiple desktop hubs (ProductHub, SettingsHub, ...) can each keep their own layout.
// `containerRef` is used to clamp positions to the desktop container's current bounds.
export function useDesktopIconPositions({
  icons,
  storageKey,
  containerRef,
  avoidRef,
  centered = true,
  cols = 4,
  cellWidth = 140,
  cellHeight = 132,
  iconWidth = 124,
  iconHeight = 112,
}) {
  const gridPositions = (originX, originY) => {
    const positions = {};
    icons.forEach((icon, index) => {
      positions[icon.id] = {
        x: originX + (index % cols) * cellWidth,
        y: originY + Math.floor(index / cols) * cellHeight,
      };
    });
    return positions;
  };

  // Corner-anchored fallback — used for the very first render, before `containerRef` has a
  // real DOM node to measure, and as the base grid that a saved layout gets merged on top of.
  const defaultPositions = () => gridPositions(12, 12);

  // The nicer, centered default — used once `containerRef` actually has something to
  // measure (the mount effect below, and handleReset, which by definition only ever runs
  // after mount). Falls back to the corner if the container isn't measurable yet.
  //
  // `avoidRef`, if given (the desktop's watermark logo), is treated as an exclusion band:
  // dead-centering the grid would land the icons right on top of it, so instead the grid
  // centers itself in whichever is bigger — the strip above the watermark or the strip
  // below it — rather than in the watermark's own space.
  const centeredDefaultPositions = () => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return defaultPositions();
    const usedCols = Math.min(icons.length, cols);
    const rows = Math.ceil(icons.length / cols);
    const gridWidth = usedCols * cellWidth;
    const gridHeight = rows * cellHeight;
    const originX = Math.max(12, (bounds.width - gridWidth) / 2);

    const avoidBounds = avoidRef?.current?.getBoundingClientRect();
    if (avoidBounds) {
      const avoidTop = avoidBounds.top - bounds.top;
      const avoidBottom = avoidBounds.bottom - bounds.top;
      const spaceAbove = Math.max(0, avoidTop);
      const spaceBelow = Math.max(0, bounds.height - avoidBottom);
      const originY =
        spaceAbove >= spaceBelow
          ? Math.max(12, (spaceAbove - gridHeight) / 2)
          : avoidBottom + Math.max(12, (spaceBelow - gridHeight) / 2);
      return gridPositions(originX, originY);
    }

    const originY = Math.max(12, (bounds.height - gridHeight) / 2);
    return gridPositions(originX, originY);
  };

  const loadPositions = () => {
    const defaults = defaultPositions();
    const saved = localStorage.getItem(storageKey);
    if (!saved) return defaults;
    try {
      const parsed = JSON.parse(saved);
      const merged = { ...defaults };
      icons.forEach((icon) => {
        const value = parsed?.[icon.id];
        if (value && typeof value.x === "number" && typeof value.y === "number") {
          merged[icon.id] = value;
        }
      });
      return merged;
    } catch (e) {
      localStorage.removeItem(storageKey);
      return defaults;
    }
  };

  const [positions, setPositions] = useState(loadPositions);
  const [hadSavedPositions] = useState(() => !!localStorage.getItem(storageKey));

  // A handful of icons on a wide desktop canvas reads as "stranded in the corner" when
  // pinned to (12, 12) — center the default grid in the actual container instead, once it's
  // mounted and measurable (can't do this in the lazy state initializer above: `containerRef`
  // isn't attached to a DOM node yet on the very first render). Only for a user who hasn't
  // dragged anything yet — someone's own saved layout is never moved out from under them.
  // `centered: false` (SettingsHub, which has enough icons to already fill the grid) opts
  // out entirely and keeps the classic top-left corner anchor.
  useEffect(() => {
    if (!centered || hadSavedPositions) return;

    const applyCenteredPositions = () => {
      const bounds = containerRef.current?.getBoundingClientRect();
      if (!bounds) return;
      setPositions(centeredDefaultPositions());
    };

    // On an uncached first load, the watermark <img> (`avoidRef`) often hasn't finished
    // loading by the time this effect runs, so its bounding rect is wrong (collapsed/
    // misplaced) — that skews the above-vs-below band comparison and can land the grid on
    // the wrong side of the watermark. "Reset Layout" never showed this because by the time
    // someone clicks it the image has long since loaded. Wait for `load` before measuring.
    const img = avoidRef?.current;
    if (img && !img.complete) {
      img.addEventListener("load", applyCenteredPositions, { once: true });
      return () => img.removeEventListener("load", applyCenteredPositions);
    }

    applyCenteredPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = ({ active, delta }) => {
    if (!delta.x && !delta.y) return;
    setPositions((prev) => {
      const current = prev[active.id];
      const bounds = containerRef.current?.getBoundingClientRect();
      const maxX = bounds ? bounds.width - iconWidth : Infinity;
      const maxY = bounds ? bounds.height - iconHeight : Infinity;
      const next = {
        ...prev,
        [active.id]: {
          x: Math.min(Math.max(current.x + delta.x, 0), maxX),
          y: Math.min(Math.max(current.y + delta.y, 0), maxY),
        },
      };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const handleReset = () => {
    localStorage.removeItem(storageKey);
    setPositions(centered ? centeredDefaultPositions() : defaultPositions());
  };

  return { positions, handleDragEnd, handleReset };
}
